import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { BASE_URL } from "../../config";
import Table from '../../components/table';
import axios from 'axios';
import moment from 'moment';
import DropDown from '../../components/select';
import MonthYearPicker from '../../components/monthYearPicker';
import { BarChart, Bar } from 'recharts';
import authStorage from "../../utils/authStorage";
import {
    aggregateZoneTotals,
    buildParticipantRecord,
    buildSummaryGoalsRows,
    summaryGoalsFileName,
    summaryGoalsSheetName,
    CATEGORY_ACHIEVED,
    CATEGORY_SHORTFALL,
} from "../../helper/summaryGoalsReport";
import downloadExcelSheet from "../../helper/downloadExcel";
import { rosterAtMonthEnd as rosterForMonth, joinersDuringMonth } from "../../helper/companyRoster";
import downloadInvoicePdf from "../../helper/downloadInvoicePdf";
import { BillingSummary, GoalsSummary } from "../../components/companyReportCharts";

const CompanyReport = () => {

    useEffect(() => {
        let isAdmin = ['admin'].includes(authStorage.getItem('roles'));
            
        // If roles is not admin, redirect to dashboard page
        if (!isAdmin) {
            window.location = window.location.origin + '/dashboard';
            return false;
        }
    });

    const navigate = useNavigate();
    const [companyList, setCompanyList] = useState([]);
    const [userList, setUserList] = useState([]);
    const [rowHeaderData, setRowHeaderData] = useState([]);
    const [firstSelected, setFirstSelected] = useState('');
    const [selected, setSelected] = useState();
    const [userDetails, setUserDetails] = useState({});
    const [totalUserAcheivedGoal, setTotalUserAcheivedGoal] = useState([]);
    // Per-participant report rows for the selected company and month.
    const [participants, setParticipants] = useState([]);
    const [pagination, setPagination] = useState({
        page: 1,
        offset: 0,
        itemsPerPage: 10,
        totalCount: 0,
    });
    const [loading, setLoading] = useState([]);

    const [discount, setDiscount] = useState("");

    /*
     * Billing now comes from the CORPORATE BILLING ENGINE, not from this screen.
     *
     * It used to price the company here: a flat rate looked up from
     * `employeeRangeConstant` by the `employeeRange` the company TYPED AT SIGNUP,
     * multiplied by a head count. That produced a different answer from the
     * Settings > Corporate Billing figures for the same company on the same day —
     * two prices derivable from one database. It also meant a company that
     * declared itself "10k-99999" at signup was billed $3/user on 23 people.
     *
     * The whole figure, including the per-band working, is now served by
     * `/billing/company/:id`, which is the same code that writes the stored
     * invoices. Nothing on this screen does billing arithmetic except applying
     * the discount below.
     */
    const [billing, setBilling] = useState(null);
    const [billingError, setBillingError] = useState('');

    /*
     * Which month the Summary & Goals figures and downloads cover. `YYYY-MM`.
     *
     * Billing deliberately does NOT follow this. The billing endpoint reports the
     * company's CURRENT plan and live active-user count -- no historical billing
     * is stored anywhere -- so dating those numbers to a past month would produce
     * a file that looks historical and is not.
     */
    const [selectedMonth, setSelectedMonth] = useState(moment().format('YYYY-MM'));

    const companyLabel = (userDetails?.companyName || 'Company').replace(/[^\w-]+/g, '-');

    /**
     * Months this company can be asked about: from the month it joined up to the
     * current one, newest first.
     *
     * Offering anything earlier would invite a download of a month the company
     * did not exist for, which comes back as an empty report that reads like a
     * data loss rather than a date that never applied. Capped at 24 so the list
     * stays usable, and falls back to the current month alone until the company
     * record has loaded.
     */
    const availableMonths = (() => {
        const current = moment().startOf('month');
        const joined = userDetails?.createdAt
            ? moment(userDetails.createdAt).startOf('month')
            : current.clone();
        const out = [];
        const cursor = current.clone();
        while (cursor.isSameOrAfter(joined, 'month') && out.length < 24) {
            out.push(cursor.format('YYYY-MM'));
            cursor.subtract(1, 'month');
        }
        return out.length ? out : [current.format('YYYY-MM')];
    })();

    // The company's own login is a management account, not a participant.
    const OWN_EMAIL = userDetails?.email;

    const monthLabel = (m) => moment(`${m}-01`, 'YYYY-MM-DD').format('MMMM YYYY');
    const isCurrentMonth = selectedMonth === moment().format('YYYY-MM');

    /*
     * The two cards count different days on purpose -- see
     * src/helper/companyRoster.js, which carries the reasoning and the tests.
     */
    const rosterAtMonthEnd = rosterForMonth(userList, OWN_EMAIL, selectedMonth);
    const joinedDuringMonth = joinersDuringMonth(rosterAtMonthEnd.length, billing?.counts?.accepted);


    /*
     * Derived AFTER the state they read — a const referenced above its own
     * declaration is a temporal-dead-zone error that blanks the page.
     *
     * The discount is the ONLY arithmetic this screen still does. It is applied
     * to the engine's total rather than to a per-user rate, because with
     * progressive tiers there is no single rate to discount: members are spread
     * across bands at different prices.
     */
    const discountPercent = discount ? parseFloat(discount) : 0;
    const billSubtotal = Number(billing?.total || 0);
    const billTotal =
        discountPercent > 0 ? billSubtotal - (billSubtotal * discountPercent) / 100 : billSubtotal;
    // Bands nobody falls into are dropped everywhere they are shown: a 23-person
    // company has no business seeing a $2 tier for 100,000 users on its invoice.
    const usedBillingLines = (billing?.lines || []).filter((l) => Number(l.users) > 0);
    const bandLabel = (l) =>
        l.to === null || l.to === undefined
            ? `${Number(l.from).toLocaleString()}+`
            : `${Number(l.from).toLocaleString()}–${Number(l.to).toLocaleString()}`;
    const dollars = (n) =>
        `$${Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}`;



    /**
     * The month's bill as a spreadsheet: ONE ROW PER TIER, then the totals.
     *
     * The old single-row shape cannot express progressive pricing — a reader
     * given one "Price Per User" would multiply it by the head count and get a
     * different number from the total. The bands are the working, so they are
     * the sheet.
     */
    const downloadBillingReport = async () => {
        if (!billing) return;

        /*
         * Laid out the way the client's own Billing.xlsx is: a banner, the
         * invoice's facts as label/value pairs, then a section bar over the
         * per-band working. The flat header-row-plus-values shape it replaces
         * could not express progressive pricing -- a reader handed one "price per
         * user" multiplies it by the head count and gets a different number from
         * the total.
         */
        const rows = [];
        const sectionRows = [];
        const headerRows = [];
        const labelRows = [];
        const highlightRows = [];
        const noteRows = [];
        /*
         * `highlight` is reserved for the figures a reader opens the file to
         * find. Marking more than a couple would defeat it -- emphasis only
         * works while most of the sheet is unemphasised.
         */
        const push = (row = [], kind) => {
            if (kind === 'section') sectionRows.push(rows.length);
            if (kind === 'header') headerRows.push(rows.length);
            if (kind === 'label') labelRows.push(rows.length);
            if (kind === 'highlight') highlightRows.push(rows.length);
            if (kind === 'note') noteRows.push(rows.length);
            rows.push(row);
        };

        push(['Company', userDetails?.companyName || '-'], 'label');
        push(['Billing Month', monthLabel(selectedMonth)], 'label');
        push(['Invoice Date', billing.invoiceDate
            ? moment.utc(billing.invoiceDate).format('DD MMM YYYY')
            : '-'], 'label');
        push(['Billed On', billing.billableModeLabel || billing.billableMode || '-'], 'label');
        push(['Pricing', billing.planVersion ? `Version ${billing.planVersion}` : 'Built-in rates'], 'label');
        push(['Status', billing.source === 'invoice' ? 'Finalised' : 'Not yet finalised'], 'label');
        push();

        push(['MEMBERS'], 'section');
        push(['Billable Members', Number(billing.billableUsers || 0)], 'highlight');
        push(['Active', Number(billing.counts?.active || 0)], 'label');
        push(['Inactive', Number(billing.counts?.inactive || 0)], 'label');
        push();

        push(['TIER BREAKDOWN'], 'section');
        push(['Tier', 'Users From', 'Users To', 'Rate Per User', 'Users In Tier', 'Amount'], 'header');
        usedBillingLines.forEach((l) => {
            push([
                `Tier ${l.tier}`,
                Number(l.from),
                l.to === null || l.to === undefined ? 'no limit' : Number(l.to),
                Number(l.rate),
                Number(l.users),
                Number(l.charge),
            ]);
        });
        push();

        push(['TOTAL'], 'section');
        push(['Subtotal', billSubtotal], 'label');
        push(['Discount (%)', discountPercent || 'N/A'], 'label');
        push(['Total Bill Amount', billTotal], 'highlight');
        push(['Average Per User', Number(billing.blendedRate || 0)], 'label');
        push();
        push([
            billing.source === 'invoice'
                ? `This is the FINALISED invoice for ${monthLabel(selectedMonth)}. It is not recalculated \u2014 the rates and head count are the ones it was issued with.`
                : `Calculated for ${monthLabel(selectedMonth)} using the head count as that month opened and the pricing in force on the 1st.`,
        ], 'note');
        push([
            'Pricing is progressive: each band is charged at its own rate, so "Average Per User" is an average and not a rate.',
        ], 'note');

        downloadExcelSheet({
            title: 'Corporate Billing',
            subtitle: `${userDetails?.companyName || 'Company'}  \u00b7  ${monthLabel(selectedMonth)}`,
            rows,
            sectionRows,
            headerRows,
            labelRows,
            highlightRows,
            // The Amount column is the answer; the rest of each tier row is the
            // working that produced it.
            boldColumns: [5],
            noteRows,
            // Money as money and head counts as whole numbers, so the figures are
            // usable in a formula rather than needing to be reformatted by hand.
            numberFormats: { 1: '#,##0', 2: '#,##0', 3: '$#,##0.00', 4: '#,##0', 5: '$#,##0.00' },
            fileName: `${companyLabel}-${moment(`${selectedMonth}-01`, 'YYYY-MM-DD').format('MMM-YYYY')}-Billing-Report.xlsx`,
            sheetName: 'Billing',
        }).catch((err) => console.log('Billing spreadsheet failed', err));
    };

    /**
     * The same billing figures as the Excel export, laid out as a document.
     *
     * Deliberately a SEPARATE function rather than a flag on the Excel one: the
     * spreadsheet is for working with the numbers and the PDF is for filing or
     * forwarding, so they want different shapes. What they must share is the
     * arithmetic, which is why both read the same `finalPrice` / headcount values
     * computed here rather than recomputing.
     */
    const downloadInvoice = async () => {
        if (!billing) return;
        try {
            await downloadInvoicePdf({
                companyName: userDetails?.companyName,
                billingMonth: monthLabel(selectedMonth),
                billedOn: billing.billableModeLabel || billing.billableMode,
                billableUsers: billing.billableUsers,
                lines: billing.lines,
                subtotal: billSubtotal,
                discountPercent: discountPercent || null,
                total: billTotal,
                blendedRate: billing.blendedRate,
                notes: [
                    billing.source === 'invoice'
                        ? `This is the finalised invoice for ${monthLabel(selectedMonth)}; it is not recalculated, so the rates and head count are the ones it was issued with.`
                        : `Head count is the roster as ${monthLabel(selectedMonth)} opened. Members joining or leaving during a billable month do not change that month's invoice.`,
                    'Pricing is progressive: each band is charged at its own rate. The average per user is an average across the bands, not a rate.',
                    ...(billing.billableMode === 'active'
                        ? [`A member counts as active with recorded exercise in the last ${billing.inactiveLookbackMonths} months, including the current one.`]
                        : []),
                ],
                fileName: `${companyLabel}-${moment(`${selectedMonth}-01`, 'YYYY-MM-DD').format('MMM-YYYY')}-Invoice.pdf`,
            });
        } catch (err) {
            console.log('Invoice PDF failed', err);
        }
    };

    /**
     * Summary & Goals report, downloaded per category so the recognition list and
     * the follow-up list can be handed out separately:
     *   CATEGORY_ACHIEVED  — reached their monthly goal
     *   CATEGORY_SHORTFALL — produced qualifying activity but fell short
     *
     * Both files carry the same header, summary and device breakdown for the whole
     * connected population, and differ only in which participants are detailed.
     * Connected members who produced no qualifying minutes are the only ones
     * absent from every detail table; they stay in the summary so the totals
     * reconcile against the participant count.
     */
    const downloadUserReport = (category) => {
        const { rows, boldRows, boldLabelRows, sectionRows, totalRows, title } = buildSummaryGoalsRows({
            companyName: userDetails?.companyName,
            participants,
            category,
        });
        /*
         * The helper stamps the period and names the file from TODAY, which is
         * wrong for any other month -- an August file would read "Reporting
         * Period: 01 Oct to 31 Oct". Both are corrected here rather than in
         * `src/helper/summaryGoalsReport.js`, which is a separate module this
         * page must not change.
         *
         * The period row is found by its LABEL, not by index: the helper builds
         * its blocks conditionally and says so, and a hardcoded row number would
         * silently rewrite the wrong line the first time a block moves.
         */
        const periodRow = rows.find((r) => r && r[0] === 'Reporting Period');
        if (periodRow) {
            const start = moment(`${selectedMonth}-01`, 'YYYY-MM-DD');
            periodRow[1] =
                `${start.clone().startOf('month').format('DD MMM YYYY')} to ` +
                `${start.clone().endOf('month').format('DD MMM YYYY')}`;
        }

        downloadExcelSheet({
            title,
            // The helper dates the subtitle from TODAY for the same reason it does
            // the period row; corrected here alongside it.
            subtitle: `${userDetails?.companyName || 'Company'}  ·  ${monthLabel(selectedMonth)}`,
            rows,
            boldRows,
            boldLabelRows,
            sectionRows,
            totalRows,
            // Swap the helper's current-month token for the selected one. Anchored
            // to the `-MMM-YYYY-Report.xlsx` suffix rather than replacing the bare
            // token: the company name comes FIRST in that string, and a plain
            // replace would rewrite the name instead for any company whose name
            // happened to contain the token. No-op for the current month.
            fileName: summaryGoalsFileName(userDetails?.companyName, category)
                .replace(
                    /-[A-Za-z]{3}-\d{4}-Report\.xlsx$/,
                    `-${moment(`${selectedMonth}-01`, 'YYYY-MM-DD').format('MMM-YYYY')}-Report.xlsx`
                ),
            sheetName: summaryGoalsSheetName(category),
        }).catch((err) => console.log('Summary & Goals spreadsheet failed', err));
    };

    const achieverCount = participants.filter((r) => r.achievedGoal).length;
    const shortfallCount = participants.filter(
        (r) => !r.achievedGoal && r.hasQualifiedActivity
    ).length;

    const getCompanyList = () => {
        setLoading(true);
        axios({
            headers: {
                "x-access-token": authStorage.getItem("auth-token")
            },
            url: `${BASE_URL}/admin/company`,
            method: 'get',
        }).then((res) => {  
            setCompanyList(res?.data?.data || []);
            setFirstSelected(res?.data?.data?.[0]);
            setPagination({ ...pagination, totalCount: res?.data?.count || 0 });
        });
    };

    const getCompanyReport = () => {
        axios({
            headers: {
                "x-access-token": authStorage.getItem("auth-token"),
            },
            url: `${BASE_URL}/admin/company/report/${selected?._id}`, //your url
            method: "GET",
        }).then((res) => {
            setUserDetails(res?.data?.data);
        });
    };

    const getCompanyUsers = () => {
        axios({
            headers: {
                "x-access-token": authStorage.getItem("auth-token"),
            },
            url: `${BASE_URL}/admin/company/user/list/${selected?._id}`, //your url
            method: "GET",
        }).then((res) => {
            console.log("comapny report fucntion calln : ",res?.data )
            setUserList(res?.data?.data);
        });
    };

    /**
     * The month's bill, from the billing engine.
     *
     * Re-read when the month changes, because unlike the old flat-rate figures
     * these ARE dated: the head count is the roster as that month opened and the
     * rates are the version in force on that day. A finalised month comes back
     * as the stored invoice rather than a fresh calculation, so the screen shows
     * what was actually billed instead of what today's roster would cost.
     */
    const getCompanyBilling = () => {
        if (!selected?._id) return;
        setBillingError('');
        axios({
            headers: { "x-access-token": authStorage.getItem("auth-token") },
            url: `${BASE_URL}/billing/company/${selected._id}?month=${selectedMonth}`,
            method: "GET",
        })
            .then((res) => setBilling(res?.data?.data || null))
            .catch((err) => {
                setBilling(null);
                setBillingError(err?.response?.data?.message || 'Could not load billing for this month.');
            });
    };

    const userTableRowTitle = [
        { title: "Date", key: "date", formatData: (data) => moment(data).format('DD MMM YYYY') },
        { title: "Start Time", key: "startTime" },
        { title: "End Time", key: "endTime" },
        { title: "Booked By", key: "user", formatData: (data) => data ? data?.email : '' },
    ];

    useEffect(() => {
        window.scrollTo({
            top: 0,
            behavior: 'smooth', // Optional: adds smooth scrolling animation
        });
        setRowHeaderData(userTableRowTitle);
        getCompanyList();
    }, []);

    useEffect(() => {
        if (firstSelected?._id) {
            getCompanyReport();
            getCompanyUsers();
        }
    }, [selected]);

    // Billing also follows the MONTH, which the two calls above do not: they
    // return the company's current state regardless of the month picker.
    useEffect(() => {
        if (firstSelected?._id) getCompanyBilling();
    }, [selected, selectedMonth, firstSelected]);

    const processUser = async (data) =>
        buildParticipantRecord(data, await getUserActivityRecord(data));

    useEffect(() => {
        // let completedUserGoalList = [];
        // userList?.map(async (data) => {
        //     let userRecord = await getUserActivityRecord();
        //     let { vigorous, moderate, light } = userRecord?.tempTimeConsumed;
        //     let tempMonthlyTarget = 0;
        //     let totalTime = 600 + moderate;
        //     if (vigorous === 0) {
        //         tempMonthlyTarget = 600;
        //     }
        //     if (vigorous >= 1) {
        //         tempMonthlyTarget = 300;
        //     }
        //     if ((tempMonthlyTarget - totalTime) < 0) {
        //         console.log(data, '+++++++==');
        //         completedUserGoalList.push(data);
        //     }
        //     console.log(totalTime, '-------', tempMonthlyTarget);
        // });
        // Assuming userList is an array

        if (!userList || (userList?.length <= 0)) {
            return;
        }

        async function processData() {
            // Already filtered for the company's own login and dated to the
            // selected month -- see rosterAtMonthEnd.
            const members = rosterAtMonthEnd;

            const records = (await Promise.all(members.map(processUser))).filter(Boolean);

            setParticipants(records);
            // Kept as user objects: the Achieved Goal tile reads .length off this.
            setTotalUserAcheivedGoal(records.filter((r) => r.achievedGoal).map((r) => r.user));
            setLoading(false);
        }
        processData();
    }, [userList, userDetails, selectedMonth]);

    // (timeToCompleteGoalPercentage <= 0);

    const getUserActivityRecord = async (data) => {
        setLoading(true);
        // Bounds come from the selected month, not from today, so a past month
        // reports what actually happened in it.
        const monthStart = moment(`${selectedMonth}-01`, 'YYYY-MM-DD');
        let startDate = monthStart.clone().startOf('month').format('YYYY-MM-DD');
        let endDate = monthStart.clone().endOf('month').format('YYYY-MM-DD');
        const fitbitUrl = (data?.fitBitId) ? `${BASE_URL}/user/getfitbitexercisedata/${startDate}/${endDate}/${data?.fitBitId}` : '';
        const garminUrl = `${BASE_URL}/user/getgarminexercisedatabydate/${startDate}/${endDate}/${data?._id}`;

        let fitbitData = fitbitUrl ? await axios({
            headers: {
                "x-access-token": authStorage.getItem("auth-token")
            },
            url: fitbitUrl,
            method: 'GET'
        }) : '';

        let garminData = await axios({
            headers: {
                "x-access-token": authStorage.getItem("auth-token")
            },
            url: garminUrl,
            method: 'GET'
        });

        // Apple members were previously invisible here — this screen only ever
        // fetched Fitbit and Garmin, so an Apple user reported zero minutes while
        // their own Summary & Goals page showed real activity. summaryGoals.jsx
        // substitutes Apple data for Garmin rather than merging, so match that.
        if (data?.isAppleConnected) {
            const appleData = await axios({
                headers: {
                    "x-access-token": authStorage.getItem("auth-token")
                },
                url: `${BASE_URL}/apple/getapplehealthdatabydate/${startDate}/${endDate}/${data?._id}`,
                method: 'GET'
            }).catch(() => null);
            if (appleData) {
                garminData = appleData;
            }
        }

        fitbitData = fitbitData?.data?.data;
        garminData = garminData?.data?.data;

        setLoading(false);
        return aggregateZoneTotals(fitbitData, garminData);
    };

    return (
        <>
            <div className="md:mx-10 md:h-auto px-4 sm:px-0">
            <h2 className="mob-header font-medium mb-5 mt-5">Company/Report</h2>

                {companyList && companyList.length > 0 && (
                    <div className="flex align-center items-center justify-between border-b border-blue-200 border-opacity-20 mt-10">
                        <div className="flex items-center">
                            <div className="text-sm tracking-tight font-light text-gray-500 mr-2">
                                Select Company:{' '}
                            </div>
                            <DropDown selected={selected} setSelected={setSelected} dataList={companyList} firstSelected={firstSelected} />
                        </div>
                        {/* Page-wide. This governs the billing figures AND the Summary
                            & Goals figures and both downloads, so it sits beside the
                            company picker rather than inside one card.

                            A calendar picker rather than a flat list of months: the list
                            is bounded by the company's join month, which is thirteen
                            entries today and keeps growing. Year arrows plus a month grid
                            stay the same size however long the company has been running. */}
                        <div className="flex items-center">
                            <div className="text-sm tracking-tight font-light text-gray-500 mr-2">
                                Month :
                            </div>
                            <MonthYearPicker
                                value={selectedMonth}
                                onChange={setSelectedMonth}
                                minMonth={availableMonths[availableMonths.length - 1]}
                                maxMonth={availableMonths[0]}
                            />
                        </div>
                    </div>
                )}


                <div className='p-4 md:p-6 bg-white rounded-lg mt-6'>
                    <div className="text-md md:text-lg text-black">
                        Welcome, {authStorage.getItem('user-name')} !
                        <br />
                        {/* <span className='text-gray-400 mt-2 block'>Thank you for being a part of the CFT program and training. Here is your overall journey with us!</span> */}
                    </div>
                </div>
                <div className="flex flex-wrap items-stretch justify-center md:py-10 py-5">
                    <div className="flex flex-1 min-w-0 flex-col md:mr-10 mr-0 my-2 text-left text-gray-300 bg-white rounded-lg shadow dark:border-gray-600 xl:p-8 md:p-6 p-4 dark:bg-gray-800 dark:text-white">
                        <div className="flex justify-between border-b border-blue-200 border-opacity-20 mb-5 pb-2">
                            <div className="text-xl tracking-tight font-medium text-black dark:text-white">
                                Billing
                            </div>
                        </div>
                        {/* ----------------------------------------------------------
                            Served by the billing engine, and drawn rather than
                            tabulated: the bill's headline is one number, so it is a
                            hero number; the split of the head count across pricing
                            bands is part-to-whole, so it is a proportional stacked
                            bar. See src/components/companyReportCharts.jsx for why
                            those forms and why those colours.
                            ---------------------------------------------------------- */}
                        <div className="flex-1">
                            {billingError ? (
                                <p className="text-sm text-left" style={{ color: '#b91c1c' }}>{billingError}</p>
                            ) : !billing ? (
                                <p className="text-sm text-left text-gray-400">Loading billing&hellip;</p>
                            ) : (
                                <>
                                    {/* An unbilled month is still REPORTED, not blanked:
                                        the roster and the activity are real, only the charge
                                        is waived. This branch used to print one sentence into
                                        an otherwise empty card.

                                        Worded from the CONFIGURED free period, not from the
                                        old assumption of exactly one month. */}
                                    <BillingSummary
                                        billing={billing}
                                        lines={usedBillingLines}
                                        subtotal={billSubtotal}
                                        total={billSubtotal}
                                        discountPercent={discountPercent}
                                        monthLabel={monthLabel(selectedMonth)}
                                        joinedDuringMonth={joinedDuringMonth}
                                        joinedNote={`billed from ${monthLabel(
                                            moment(`${selectedMonth}-01`, 'YYYY-MM-DD').add(1, 'month').format('YYYY-MM')
                                        )}`}
                                        notBilled={!billing.billableThisMonth}
                                        notBilledNote={
                                            billing.freeOnboardingMonths === 1
                                                ? `Free onboarding month · billing starts ${billing.firstBillableMonth ? monthLabel(billing.firstBillableMonth) : 'the following month'}`
                                                : `Inside a ${billing.freeOnboardingMonths}-month free onboarding period · billing starts ${billing.firstBillableMonth ? monthLabel(billing.firstBillableMonth) : 'the following month'}`
                                        }
                                    />
                                    {!billing.billableThisMonth && billing.connectionDateSource === 'created' && (
                                        <p className="mt-3 text-xs text-left text-gray-400">
                                            Measured from when the company record was created. If that is not
                                            when they went live, set a connection date under Settings &rsaquo;
                                            Corporate Billing.
                                        </p>
                                    )}
                                    {billing.source === 'invoice' && (
                                        <p className="mt-4 text-xs text-left" style={{ color: '#047857' }}>
                                            Finalised invoice &mdash; not recalculated. These are the rates and the
                                            head count it was issued with.
                                        </p>
                                    )}
                                </>
                            )}
                        </div>

                        <div className="mt-5 flex flex-wrap items-end justify-between gap-4 border-0 border-t border-solid border-gray-100 pt-4 dark:border-gray-700">
                            <div className="text-left">
                                <label htmlFor="billing-discount" className="block text-xs text-gray-500 dark:text-gray-300">
                                    Discount (%)
                                </label>
                                <input
                                    id="billing-discount"
                                    type="number"
                                    min="0"
                                    max="100"
                                    placeholder="0"
                                    value={discount}
                                    onChange={(e) => setDiscount(e.target.value)}
                                    className="mt-1 w-24 px-2 py-1 text-sm text-gray-900"
                                />
                            </div>
                            <div className="flex flex-col items-end gap-1">
                            <div onClick={() => downloadBillingReport()} className="flex text-[#0694F3] font-medium cursor-pointer md:text-base text-xs">
                                Invoice (XLS)
                                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="#0694F3" class="w-5 h-5 ml-2">
                                    <path fill-rule="evenodd" d="M12 2.25a.75.75 0 01.75.75v11.69l3.22-3.22a.75.75 0 111.06 1.06l-4.5 4.5a.75.75 0 01-1.06 0l-4.5-4.5a.75.75 0 111.06-1.06l3.22 3.22V3a.75.75 0 01.75-.75zm-9 13.5a.75.75 0 01.75.75v2.25a1.5 1.5 0 001.5 1.5h13.5a1.5 1.5 0 001.5-1.5V16.5a.75.75 0 011.5 0v2.25a3 3 0 01-3 3H5.25a3 3 0 01-3-3V16.5a.75.75 0 01.75-.75z" clip-rule="evenodd" />
                                </svg>
                            </div>
                            <div
                                onClick={() => downloadInvoice()}
                                title="Download the invoice as a PDF"
                                className="flex items-center font-medium cursor-pointer md:text-sm text-xs"
                                style={{ color: '#E53E14' }}
                            >
                                Invoice (PDF)
                                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-4 h-4 ml-1.5"><path fillRule="evenodd" d="M5.625 1.5c-1.036 0-1.875.84-1.875 1.875v17.25c0 1.035.84 1.875 1.875 1.875h12.75c1.035 0 1.875-.84 1.875-1.875V12.75A3.75 3.75 0 0 0 16.5 9h-1.875a1.875 1.875 0 0 1-1.875-1.875V5.25A3.75 3.75 0 0 0 9 1.5H5.625ZM7.5 15a.75.75 0 0 1 .75-.75h7.5a.75.75 0 0 1 0 1.5h-7.5A.75.75 0 0 1 7.5 15Zm.75 2.25a.75.75 0 0 0 0 1.5H12a.75.75 0 0 0 0-1.5H8.25Z" clipRule="evenodd" /><path d="M12.971 1.816A5.23 5.23 0 0 1 14.25 5.25v1.875c0 .207.168.375.375.375H16.5a5.23 5.23 0 0 1 3.434 1.279 9.768 9.768 0 0 0-6.963-6.963Z" /></svg>
                            </div>
                            </div>
                        </div>
                    </div>
                    <div className="flex md:w-[40%] w-[100%] min-w-0 flex flex-col my-2 text-left text-gray-300 bg-white rounded-lg shadow dark:border-gray-600 xl:p-8 md:p-6 p-4 dark:bg-gray-800 dark:text-white">
                        <div className="flex justify-between border-b border-blue-200 border-opacity-20 mb-5 pb-2">
                            <div className="text-xl tracking-tight font-medium text-black dark:text-white">
                                Summary & Goals
                            </div>
                        </div>

                        <div className="flex-1 pt-1">
                            {/* TWO QUESTIONS, TWO FORMS. The previous pair of bare
                                rectangles compared "achieved" against "total users"
                                and could not answer either: a company where everyone
                                reached 95% and one where everyone reached 5% drew
                                exactly the same picture. */}
                            <GoalsSummary
                                participants={participants}
                                monthLabel={monthLabel(selectedMonth)}
                                inProgress={isCurrentMonth}
                            />
                        </div>

                        {/* One download per reporting category: the recognition
                            list and the follow-up list are used separately. Laid
                            out on a single row so the fixed-height card does not
                            overflow. */}
                        <div className="flex flex-wrap justify-between items-center gap-x-6 gap-y-1 pt-4">
                            {/* Naming the month on the row stops a past-month file being
                                mistaken for this month's once it is off the screen. */}
                            <span className="text-xs text-gray-500">
                                {isCurrentMonth
                                    ? `${monthLabel(selectedMonth)} (in progress)`
                                    : monthLabel(selectedMonth)}
                            </span>
                            <div className="flex flex-wrap items-center gap-x-6 gap-y-1">
                            <div
                                onClick={() => downloadUserReport(CATEGORY_ACHIEVED)}
                                title="Download the Goal Achievers report"
                                className={`flex items-center font-medium cursor-pointer text-sm ${achieverCount ? 'text-[#0694F3]' : 'text-gray-400'}`}
                            >
                                Goal Achievers ({achieverCount})
                                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-4 h-4 ml-1.5">
                                    <path fillRule="evenodd" d="M12 2.25a.75.75 0 01.75.75v11.69l3.22-3.22a.75.75 0 111.06 1.06l-4.5 4.5a.75.75 0 01-1.06 0l-4.5-4.5a.75.75 0 111.06-1.06l3.22 3.22V3a.75.75 0 01.75-.75zm-9 13.5a.75.75 0 01.75.75v2.25a1.5 1.5 0 001.5 1.5h13.5a1.5 1.5 0 001.5-1.5V16.5a.75.75 0 011.5 0v2.25a3 3 0 01-3 3H5.25a3 3 0 01-3-3V16.5a.75.75 0 01.75-.75z" clipRule="evenodd" />
                                </svg>
                            </div>
                            <div
                                onClick={() => downloadUserReport(CATEGORY_SHORTFALL)}
                                title="Download the Qualified Activity - Goal Not Reached report"
                                className={`flex items-center font-medium cursor-pointer text-sm ${shortfallCount ? 'text-[#0694F3]' : 'text-gray-400'}`}
                            >
                                Goal Not Reached ({shortfallCount})
                                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-4 h-4 ml-1.5">
                                    <path fillRule="evenodd" d="M12 2.25a.75.75 0 01.75.75v11.69l3.22-3.22a.75.75 0 111.06 1.06l-4.5 4.5a.75.75 0 01-1.06 0l-4.5-4.5a.75.75 0 111.06-1.06l3.22 3.22V3a.75.75 0 01.75-.75zm-9 13.5a.75.75 0 01.75.75v2.25a1.5 1.5 0 001.5 1.5h13.5a1.5 1.5 0 001.5-1.5V16.5a.75.75 0 011.5 0v2.25a3 3 0 01-3 3H5.25a3 3 0 01-3-3V16.5a.75.75 0 01.75-.75z" clipRule="evenodd" />
                                </svg>
                            </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </>
    )
}
export default CompanyReport;
